import React, { useEffect, useRef, useState, useCallback } from "react";
import { Card, Table, Badge, Modal, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Swal from "sweetalert2";
import imageCompression from 'browser-image-compression';
import ThaiDateInput from '../components/ThaiDateInput'

const API = process.env.REACT_APP_API;
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const PHOTO_COMPRESS_OPTS = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true, fileType: 'image/jpeg' };
const MAX_PHOTO_UPLOAD_BYTES = 950 * 1024;

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:')) return path;
  return `${UPLOAD_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

const GENDER_LABEL = { m: 'ชาย', f: 'หญิง' };
// ค่าใน tb_teacher.t_side
const SIDE_LABEL = {
  islamic:  'ศาสนา',
  academic: 'สามัญ',
  both:     'ศาสนาและสามัญ',
  ap:       'อนุบาล/ประถม',
  officer:  'เจ้าหน้าที่',
};
const SIDE_BADGE = { academic: 'info', islamic: 'success', both: 'primary', ap: 'warning', officer: 'dark' };

const emptyForm = { t_idnumber: '', t_name: '', t_arabicname: '', t_gen: 'm', t_phone: '', t_birthdate: '', t_side: 'academic', password: '' };
const isActive = (t) => String(t.t_status) === '1';

const TeacherAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  const src = resolveUploadUrl(photo);
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function AdminStaff() {
  const { setLoading } = useLoading();
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch]     = useState('');

  // Add/Edit modal
  const [showForm, setShowForm]   = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [form, setForm]           = useState(emptyForm);
  const [formError, setFormError] = useState('');

  // Photo upload (edit mode only)
  const photoInputRef = useRef(null);
  const [photoFile, setPhotoFile]         = useState(null);
  const [photoPreview, setPhotoPreview]   = useState('');
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto]   = useState(false);

  // Reset password modal
  const [showReset, setShowReset]     = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError]   = useState('');

  const loadTeachers = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`${API}/admin/getTeachers`)
      .then(res => setTeachers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { loadTeachers(); }, [loadTeachers]);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const filtered = teachers.filter(t =>
    t.t_name?.includes(search) ||
    t.t_idnumber?.includes(search) ||
    t.t_arabicname?.includes(search)
  );

  // ── Add ──
  const openAdd = () => {
    setForm(emptyForm);
    setFormError('');
    setEditMode(false);
    setPhotoFile(null);
    setPhotoPreview('');
    setShowForm(true);
  };

  // ── Edit ──
  const openEdit = (t) => {
    // server ส่ง t_birthdate มาเป็นสตริง yyyy-mm-dd แล้ว (DATE_FORMAT ใน getTeachers)
    // ห้ามกลับไปดึงคอลัมน์ DATE ตรงๆ แล้วตัดสิบตัวแรกที่นี่ จะได้วันก่อนหน้าหนึ่งวัน
    const bd = t.t_birthdate || '';
    setForm({ t_id: t.t_id, t_idnumber: t.t_idnumber, t_name: t.t_name, t_arabicname: t.t_arabicname || '', t_gen: t.t_gen || 'm', t_phone: t.t_phone || '', t_birthdate: bd, t_side: t.t_side || 'academic', password: '' });
    setFormError('');
    setEditMode(true);
    setPhotoFile(null);
    setPhotoPreview(resolveUploadUrl(t.t_photo));
    setShowForm(true);
  };

  const handleFormChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      Swal.fire({ icon: 'warning', title: 'ไฟล์ไม่ถูกต้อง', text: 'กรุณาเลือกรูปภาพเท่านั้น' });
      return;
    }

    const applyPreparedPhoto = (preparedFile) => {
      if (preparedFile.size > MAX_PHOTO_UPLOAD_BYTES) {
        setPhotoFile(null);
        Swal.fire({
          icon: 'warning',
          title: 'รูปยังมีขนาดใหญ่เกินไป',
          text: 'กรุณาเลือกรูปที่เล็กลง หรือครอบ/ลดขนาดรูปก่อนอัปโหลด'
        });
        return;
      }

      setPhotoFile(preparedFile);
      setPhotoPreview(prev => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(preparedFile);
      });
    };

    setProcessingPhoto(true);
    try {
      const compressed = await imageCompression(file, PHOTO_COMPRESS_OPTS);
      const jpgFile = new File(
        [compressed],
        file.name.replace(/\.[^.]+$/, '') + '.jpg',
        { type: 'image/jpeg' }
      );
      applyPreparedPhoto(jpgFile);
    } catch (error) {
      console.warn('Teacher photo compression failed, using original file', error);
      applyPreparedPhoto(file);
    } finally {
      setProcessingPhoto(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile || !form.t_id) return;

    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('teacherPhoto', photoFile);

    try {
      const response = await axiosInstance.post(`${API}/admin/uploadTeacherPhoto/${form.t_id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const photoPath = response.data.photoPath || '';
      setPhotoFile(null);
      setPhotoPreview(resolveUploadUrl(photoPath));
      if (photoInputRef.current) photoInputRef.current.value = '';
      setTeachers(prev => prev.map(t => t.t_id === form.t_id ? { ...t, t_photo: photoPath } : t));
      Swal.fire({ icon: 'success', title: 'อัปโหลดรูปสำเร็จ', timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'อัปโหลดรูปไม่สำเร็จ',
        text: error.response?.data?.message || 'กรุณาลองใหม่อีกครั้ง'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFormSave = async () => {
    setFormError('');
    if (!form.t_idnumber || !form.t_name) { setFormError('กรุณากรอกรหัสผู้ใช้และชื่อ'); return; }
    if (!editMode && !form.password) { setFormError('กรุณาตั้งรหัสผ่านเริ่มต้น'); return; }
    if (!editMode && form.password.length < 6) { setFormError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'); return; }
    try {
      if (editMode) {
        await axiosInstance.post(`${API}/admin/updateTeacher`, { t_id: form.t_id, t_name: form.t_name, t_arabicname: form.t_arabicname, t_gen: form.t_gen, t_phone: form.t_phone, t_birthdate: form.t_birthdate, t_side: form.t_side });
      } else {
        await axiosInstance.post(`${API}/admin/addTeacher`, form);
      }
      setShowForm(false);
      loadTeachers();
    } catch (err) {
      setFormError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  // ── Toggle status ──
  const toggleStatus = async (t) => {
    const newStatus = isActive(t) ? 0 : 1;
    try {
      await axiosInstance.post(`${API}/admin/setTeacherStatus`, { t_id: t.t_id, t_status: newStatus });
      loadTeachers();
    } catch {}
  };

  // ── Reset password ──
  const openReset = (t) => {
    setResetTarget(t);
    setNewPassword('');
    setResetError('');
    setShowReset(true);
  };

  const handleReset = async () => {
    setResetError('');
    if (newPassword.length < 6) { setResetError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'); return; }
    try {
      await axiosInstance.post(`${API}/admin/resetPassword`, { t_id: resetTarget.t_id, new_password: newPassword });
      setShowReset(false);
    } catch (err) {
      setResetError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">จัดการบุคลากร</h4>
        <Button size="sm" variant="primary" onClick={openAdd}>
          <i className="ri-user-add-line me-1"></i>เพิ่มบุคลากร
        </Button>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <InputGroup style={{ maxWidth: 320 }}>
            <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
            <Form.Control placeholder="ค้นหาชื่อ / รหัสผู้ใช้" value={search} onChange={e => setSearch(e.target.value)} />
          </InputGroup>
        </Card.Body>
      </Card>

      <Card className="card-one">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 44 }}></th>
                  <th style={{ width: 120 }}>รหัสผู้ใช้</th>
                  <th>ชื่อ - นามสกุล</th>
                  <th>ชื่ออาหรับ</th>
                  <th style={{ width: 130 }}>ฝ่าย</th>
                  <th style={{ width: 70 }}>เพศ</th>
                  <th style={{ width: 130 }}>โทรศัพท์</th>
                  <th style={{ width: 90 }}>สถานะ</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
                ) : filtered.map(t => (
                  <tr key={t.t_id}>
                    <td className="align-middle"><TeacherAvatar photo={t.t_photo} name={t.t_name} /></td>
                    <td className="align-middle fw-semibold text-nowrap">{t.t_idnumber}</td>
                    <td className="align-middle">{t.t_name}</td>
                    <td className="align-middle arabic-text">{t.t_arabicname || <span className="text-muted">—</span>}</td>
                    <td className="align-middle">
                      <Badge bg={SIDE_BADGE[t.t_side] || 'secondary'}>{SIDE_LABEL[t.t_side] || '—'}</Badge>
                    </td>
                    <td className="align-middle">{GENDER_LABEL[t.t_gen] || '—'}</td>
                    <td className="align-middle text-nowrap">{t.t_phone || '—'}</td>
                    <td className="align-middle">
                      <Badge bg={isActive(t) ? 'success' : 'secondary'}>
                        {isActive(t) ? 'ใช้งาน' : 'ปิดใช้'}
                      </Badge>
                    </td>
                    <td className="align-middle text-nowrap">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(t)} title="แก้ไข">
                        <i className="ri-edit-line"></i>
                      </Button>
                      <Button size="sm" variant={isActive(t) ? 'outline-warning' : 'outline-success'} className="me-1" onClick={() => toggleStatus(t)} title={isActive(t) ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}>
                        <i className={isActive(t) ? 'ri-toggle-fill' : 'ri-toggle-line'}></i>
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => openReset(t)} title="รีเซตรหัสผ่าน">
                        <i className="ri-lock-password-line"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'แก้ไขข้อมูลบุคลากร' : 'เพิ่มบุคลากรใหม่'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          <Form>
            {editMode && (
              <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 mb-4">
                <div
                  className="border bg-light d-flex align-items-center justify-content-center overflow-hidden"
                  style={{ width: 100, height: 100, borderRadius: '50%', flexShrink: 0 }}
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt={form.t_name || 'staff'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <i className="ri-user-line fs-1 text-secondary"></i>
                  )}
                </div>
                <div className="flex-grow-1 text-center text-sm-start">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handlePhotoChange}
                    disabled={processingPhoto || uploadingPhoto}
                  />
                  <div className="d-flex flex-column flex-sm-row gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-primary"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={processingPhoto || uploadingPhoto}
                    >
                      <i className="ri-image-add-line me-1"></i>เลือกรูป
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={handlePhotoUpload}
                      disabled={!photoFile || processingPhoto || uploadingPhoto}
                    >
                      {processingPhoto || uploadingPhoto ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="ri-upload-cloud-line me-1"></i>}
                      {processingPhoto ? 'กำลังเตรียมรูป' : 'อัปโหลดรูป'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>รหัสผู้ใช้ <span className="text-danger">*</span></Form.Label>
              <Form.Control name="t_idnumber" value={form.t_idnumber} onChange={handleFormChange} disabled={editMode} placeholder="เช่น T001" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อ - นามสกุล <span className="text-danger">*</span></Form.Label>
              <Form.Control name="t_name" value={form.t_name} onChange={handleFormChange} placeholder="ชื่อภาษาไทย" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่ออาหรับ</Form.Label>
              <Form.Control name="t_arabicname" value={form.t_arabicname} onChange={handleFormChange} placeholder="ชื่อภาษาอาหรับ" className="arabic-text" dir="rtl" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เพศ</Form.Label>
              <Form.Select name="t_gen" value={form.t_gen} onChange={handleFormChange}>
                <option value="m">ชาย</option>
                <option value="f">หญิง</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ฝ่าย</Form.Label>
              <Form.Select name="t_side" value={form.t_side} onChange={handleFormChange}>
                {Object.entries(SIDE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>โทรศัพท์</Form.Label>
              <Form.Control name="t_phone" value={form.t_phone} onChange={handleFormChange} placeholder="0xx-xxx-xxxx" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>วันเกิด</Form.Label>
              <ThaiDateInput name="t_birthdate" value={form.t_birthdate} onChange={handleFormChange} />
            </Form.Group>
            {!editMode && (
              <Form.Group className="mb-3">
                <Form.Label>รหัสผ่านเริ่มต้น <span className="text-danger">*</span></Form.Label>
                <Form.Control type="password" name="password" value={form.password} onChange={handleFormChange} placeholder="อย่างน้อย 6 ตัวอักษร" />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleFormSave}>
            {editMode ? 'บันทึกการแก้ไข' : 'เพิ่มบุคลากร'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showReset} onHide={() => setShowReset(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>รีเซตรหัสผ่าน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetTarget && <p className="mb-3 fw-semibold">{resetTarget.t_name}</p>}
          {resetError && <div className="alert alert-danger py-2">{resetError}</div>}
          <Form.Group>
            <Form.Label>รหัสผ่านใหม่</Form.Label>
            <Form.Control type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="อย่างน้อย 6 ตัวอักษร" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReset(false)}>ยกเลิก</Button>
          <Button variant="danger" onClick={handleReset}>รีเซตรหัสผ่าน</Button>
        </Modal.Footer>
      </Modal>
      </div>
    </React.Fragment>
  );
}
